import { alert } from 'actions/flash'
import {
  useGetPromoCodesVisibilityQuery,
  useUpdatePromoCodesVisibilityMutation,
} from 'api/promoCodeVisibility'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { PromoCodeVisibilityOverride } from 'models/Config'
import { useCallback, useEffect } from 'react'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('cms.settings.promo_code_visibility')

export default function usePromoCodeVisibility() {
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const orderingEnabled = Boolean(merchant?.ordering_enabled)
  const { data, isLoading, isError } = useGetPromoCodesVisibilityQuery(
    undefined,
    { skip: !orderingEnabled }
  )
  const [updateVisibility, { isLoading: isSaving }] =
    useUpdatePromoCodesVisibilityMutation()

  const isHidden =
    data?.promoCodesVisibilityOverride === PromoCodeVisibilityOverride.HIDDEN

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(`alerts.${messageKey}`),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (isError) {
      displayAlert('danger', 'fetch_error')
    }
  }, [isError, displayAlert])

  const handleToggle = useCallback(
    async (checked: boolean) => {
      try {
        await updateVisibility({
          promoCodesVisibilityOverride: checked
            ? PromoCodeVisibilityOverride.HIDDEN
            : null,
        }).unwrap()
        displayAlert('success', checked ? 'hide_success' : 'show_success')
      } catch (error) {
        errorHandler.capture('Failed to save promo code visibility', {
          error,
        })
        displayAlert('danger', 'save_error')
      }
    },
    [updateVisibility, displayAlert]
  )

  return {
    isHidden,
    isLoading,
    isSaving,
    handleToggle,
  }
}
